import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GazeboWorld, GazeboModel, SimulationState, JointState, LinkState } from 'src/types/gazebo';

interface GazeboState {
    connected: boolean;
    connecting: boolean;
    error: string | null;
    world: GazeboWorld | null;
    models: GazeboModel[];
    simulationState: SimulationState | null;
    jointStates: Record<string, JointState[]>;
    linkStates: Record<string, LinkState[]>;
    selectedModel: string | null;
}

const initialState: GazeboState = {
    connected: false,
    connecting: false,
    error: null,
    world: null,
    models: [],
    simulationState: null,
    jointStates: {},
    linkStates: {},
    selectedModel: null,
};

const gazeboSlice = createSlice({
    name: 'gazebo',
    initialState,
    reducers: {
        setConnecting: (state, action: PayloadAction<boolean>) => {
            state.connecting = action.payload;
        },
        setConnected: (state, action: PayloadAction<boolean>) => {
            state.connected = action.payload;
            if (action.payload) {
                state.error = null;
                state.connecting = false;
            }
        },
        setError: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
            state.connected = false;
            state.connecting = false;
        },
        setWorld: (state, action: PayloadAction<GazeboWorld>) => {
            state.world = action.payload;
        },
        setModels: (state, action: PayloadAction<GazeboModel[]>) => {
            state.models = action.payload;
        },
        addModel: (state, action: PayloadAction<GazeboModel>) => {
            state.models.push(action.payload);
        },
        updateModel: (state, action: PayloadAction<GazeboModel>) => {
            const index = state.models.findIndex(m => m.id === action.payload.id);
            if (index !== -1) {
                state.models[index] = action.payload;
            }
        },
        removeModel: (state, action: PayloadAction<string>) => {
            state.models = state.models.filter(m => m.id !== action.payload);
        },
        setSimulationState: (state, action: PayloadAction<SimulationState>) => {
            state.simulationState = action.payload;
        },
        updateJointStates: (state, action: PayloadAction<{ modelName: string; states: JointState[] }>) => {
            state.jointStates[action.payload.modelName] = action.payload.states;
        },
        updateLinkStates: (state, action: PayloadAction<{ modelName: string; states: LinkState[] }>) => {
            state.linkStates[action.payload.modelName] = action.payload.states;
        },
        setSelectedModel: (state, action: PayloadAction<string | null>) => {
            state.selectedModel = action.payload;
        },
        reset: (state) => {
            return initialState;
        },
    },
});

export const {
    setConnecting,
    setConnected,
    setError,
    setWorld,
    setModels,
    addModel,
    updateModel,
    removeModel,
    setSimulationState,
    updateJointStates,
    updateLinkStates,
    setSelectedModel,
    reset,
} = gazeboSlice.actions;

export default gazeboSlice.reducer;